'use client';

// useEngagement - Hook for tracking user engagement and determining when to show sign-up prompts
// Story 3.8: Sign-Up Prompt After High Engagement

import { useMemo, useCallback, useState, useEffect, useRef } from 'react';

const THRESHOLDS = {
  uploads: 3,
  testQuestions: 5,
  activeMinutes: 10,
} as const;

const COOLDOWN_MS = 15 * 60 * 1000; // 15 minutes (minimum gap between any prompts)
const REMIND_LATER_MS = 15 * 60 * 1000; // 15 minutes

const DISMISSED_PROMPTS_KEY = 'boty_dismissed_prompts';

export type PromptType = 'uploads' | 'questions' | 'time';

interface DismissedPrompt {
  type: PromptType;
  dismissedAt: number;
  remindLater?: boolean;
}

export interface UseEngagementOptions {
  /** Current bot ID */
  botId: string | null;
  /** Number of uploaded content sources */
  uploadCount: number;
  /** Number of test questions asked */
  questionCount: number;
  /** Whether the user is authenticated (prompts only show for unauthenticated users) */
  isAuthenticated: boolean;
}

export interface UseEngagementReturn {
  /** Number of uploaded content sources */
  uploadCount: number;
  /** Number of test questions asked */
  questionCount: number;
  /** Number of minutes the user has been active */
  activeMinutes: number;
  /** Whether a sign-up prompt should be shown */
  shouldShowPrompt: boolean;
  /** Which type of prompt to show (null if none) */
  promptType: PromptType | null;
  /** Dismiss the current prompt */
  dismissPrompt: (type: PromptType, remindLater?: boolean) => void;
  /** The engagement thresholds */
  thresholds: typeof THRESHOLDS;
}

/**
 * Load dismissed prompts from sessionStorage
 */
function loadDismissedPrompts(): DismissedPrompt[] {
  if (typeof window === 'undefined') return [];
  try {
    const stored = sessionStorage.getItem(DISMISSED_PROMPTS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

/**
 * Save dismissed prompts to sessionStorage
 */
function saveDismissedPrompts(prompts: DismissedPrompt[]): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(DISMISSED_PROMPTS_KEY, JSON.stringify(prompts));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Hook for tracking user engagement and determining when to show sign-up prompts
 *
 * @example
 * ```tsx
 * const { shouldShowPrompt, promptType, dismissPrompt } = useEngagement({
 *   botId: 'bot-123',
 *   uploadCount: sources.length,
 *   questionCount: messages.filter(m => m.role === 'user').length,
 *   isAuthenticated: !!user,
 * });
 * ```
 */
export function useEngagement({
  botId,
  uploadCount,
  questionCount,
  isAuthenticated,
}: UseEngagementOptions): UseEngagementReturn {
  // Track session start time (when hook first mounts with a botId)
  const sessionStartTimeRef = useRef<number | null>(null);
  const [activeMinutes, setActiveMinutes] = useState(0);
  const [dismissedPrompts, setDismissedPrompts] = useState<DismissedPrompt[]>(() =>
    loadDismissedPrompts()
  );

  // Initialize session start time when botId is available
  useEffect(() => {
    if (botId && sessionStartTimeRef.current === null) {
      sessionStartTimeRef.current = Date.now();
    }
  }, [botId]);

  // Update active minutes every minute
  useEffect(() => {
    if (!botId || sessionStartTimeRef.current === null) return;

    const updateActiveMinutes = () => {
      const elapsed = Date.now() - sessionStartTimeRef.current!;
      setActiveMinutes(Math.floor(elapsed / 60000));
    };

    // Update immediately
    updateActiveMinutes();

    // Then update every minute
    const interval = setInterval(updateActiveMinutes, 60000);

    return () => clearInterval(interval);
  }, [botId]);

  // Check if prompt type is on cooldown
  const isOnCooldown = useCallback(
    (type: PromptType): boolean => {
      const dismissed = dismissedPrompts.find((p) => p.type === type);
      if (!dismissed) return false;

      const cooldown = dismissed.remindLater ? REMIND_LATER_MS : COOLDOWN_MS;
      return Date.now() - dismissed.dismissedAt < cooldown;
    },
    [dismissedPrompts]
  );

  // Global cooldown: if ANY prompt was recently dismissed, suppress all others.
  // Prevents two prompts from firing back-to-back when multiple thresholds are met.
  // Note: not memoized because the result depends on Date.now() (live), not just deps.
  const isAnyPromptOnCooldown = useCallback((): boolean => {
    if (dismissedPrompts.length === 0) return false;
    const now = Date.now();
    return dismissedPrompts.some((dismissed) => {
      const cooldown = dismissed.remindLater ? REMIND_LATER_MS : COOLDOWN_MS;
      return now - dismissed.dismissedAt < cooldown;
    });
  }, [dismissedPrompts]);

  // Determine which prompt to show (priority: uploads > questions > time)
  // Only show prompts for unauthenticated users
  const promptToShow = useMemo((): PromptType | null => {
    // Don't show prompts for authenticated users or when no bot exists
    if (isAuthenticated || !botId) return null;

    // If any prompt was recently dismissed, don't show another one yet
    if (isAnyPromptOnCooldown()) return null;

    if (uploadCount >= THRESHOLDS.uploads && !isOnCooldown('uploads')) {
      return 'uploads';
    }
    if (questionCount >= THRESHOLDS.testQuestions && !isOnCooldown('questions')) {
      return 'questions';
    }
    if (activeMinutes >= THRESHOLDS.activeMinutes && !isOnCooldown('time')) {
      return 'time';
    }
    return null;
  }, [uploadCount, questionCount, activeMinutes, isOnCooldown, isAnyPromptOnCooldown, isAuthenticated, botId]);

  // Dismiss a prompt with optional "remind later" flag
  const dismissPrompt = useCallback((type: PromptType, remindLater = false) => {
    setDismissedPrompts((prev) => {
      const updated = [
        ...prev.filter((p) => p.type !== type),
        { type, dismissedAt: Date.now(), remindLater },
      ];
      saveDismissedPrompts(updated);
      return updated;
    });
  }, []);

  return {
    uploadCount,
    questionCount,
    activeMinutes,
    shouldShowPrompt: promptToShow !== null,
    promptType: promptToShow,
    dismissPrompt,
    thresholds: THRESHOLDS,
  };
}
